import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "./Button";
import { track } from "@/lib/analytics";
import { useLocale } from "@/i18n/LocaleProvider";

/**
 * Fångar besökare som är på väg att lämna en kommersiell sida (priser, case)
 * med ett konkret erbjudande. Visas max en gång per session, aldrig på mobil
 * (där finns ingen musrörelse att läsa av) och aldrig direkt vid sidladdning.
 */
export function ExitIntentOffer({
  location,
  ctaHref = "#kontakt",
  title,
  body,
  ctaLabel,
}: {
  location: string;
  ctaHref?: string;
  title?: string;
  body?: string;
  ctaLabel?: string;
}) {
  const en = useLocale() === "en";
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 900px)").matches) return;
    const key = `fenny_exit_${location}`;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch {
      return;
    }

    let armed = false;
    const arm = window.setTimeout(() => {
      armed = true;
    }, 15000);

    function onLeave(e: MouseEvent) {
      if (!armed || e.clientY > 0 || e.relatedTarget) return;
      try {
        sessionStorage.setItem(key, "1");
      } catch {
        /* ignore */
      }
      setOpen(true);
      track("exit_intent_shown", { location });
      document.removeEventListener("mouseout", onLeave);
    }

    document.addEventListener("mouseout", onLeave);
    return () => {
      window.clearTimeout(arm);
      document.removeEventListener("mouseout", onLeave);
    };
  }, [location]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label={en ? "Close" : "Stäng"}
        onClick={() => setOpen(false)}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      <div className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-background p-8 shadow-2xl">
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={en ? "Close" : "Stäng"}
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/70 transition-colors hover:bg-white/5 hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="text-[11px] uppercase tracking-[0.2em] text-mint">
          {en ? "Before you go" : "Innan ni går"}
        </div>
        <h2 className="mt-3 text-2xl font-semibold leading-tight">
          {title ?? (en ? "Want a price on your idea?" : "Vill ni ha ett prisförslag på er idé?")}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {body ??
            (en
              ? "Describe your project in a couple of lines and we come back within 24 hours during office hours (weekdays 08:30–17:00 CET) with format, setup and a fixed price. Free, no commitment."
              : "Beskriv projektet på ett par rader så återkommer vi inom 24 timmar inom kontorstid (vardagar 08:30–17:00) med format, upplägg och fast pris. Kostnadsfritt och utan bindning.")}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            href={ctaHref}
            variant="mint"
            arrow
            onClick={() => {
              track("exit_intent_accept", { location });
              setOpen(false);
            }}
          >
            {ctaLabel ?? (en ? "Get a price proposal" : "Få ett prisförslag")}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              track("exit_intent_dismiss", { location });
              setOpen(false);
            }}
          >
            {en ? "Not now" : "Inte nu"}
          </Button>
        </div>
      </div>
    </div>
  );
}
